import React from 'react'
import { useIntl } from 'react-intl'
import { useOrganization } from 'contexts/organization'
import { useEnvironment } from 'contexts/environment'
import data from 'pages/integrations/pages/integration/data'
import { size, reduce } from 'utilities/object'
import Link from 'components/link'

export const useGetDisclaimers = () => {
    const { formatMessage } = useIntl()

    const { statuses } = useOrganization()
    const { environment } = useEnvironment()

    const meta = {
        absenceConfigured: statuses?.hasSetUpAbsenceTypes || statuses?.hasSetUpWorkSchedule
    }

    return integration => {
        if(!integration?.type) {
            return {}
        }

        let disclaimers = {
            ...(getDisclaimersByIntegration[integration.type]?.(meta) ?? {}),
            ...getGenericDisclaimers(meta)
        }

        if(!size(disclaimers)) {
            return disclaimers
        }

        const integrationData = data[integration.type]?.(environment) ?? {}

        disclaimers = reduce(disclaimers, (accumulator, disclaimer, key) => {
            if(!!disclaimer?.messages) {
                disclaimer.messages = disclaimer.messages.map(({ type, message }) => {
                    return {
                        id: message.id,
                        type,
                        message: formatMessage(
                            message, {
                            integration: integrationData.name,
                            sickLeaveLink: chunks => (
                                <Link
                                    href={`https://www.${environment.tld}/knowledge/how-to-work-with-sick-leave-follow-up`}
                                    target="_blank"
                                    className="constructive">
                                    {chunks}
                                </Link>
                            )
                        })
                    }
                })
            }

            return {
                ...accumulator,
                [key]: disclaimer
            }
        })

        return [disclaimers, meta]
    }
}

const getDisclaimersByIntegration = {
    altinn: () => ({
        lastFetchDate: {
            includeWithoutValue: true,
            messages: [
                {
                    type: 'warning',
                    message: {
                        id: 'integration_altinn_message_admin_notifications_disclaimer',
                        defaultMessage: 'Absence administrators will receive one notification per absence registered since this date, for all the employees they manage absence for.'
                    }
                },
                {
                    type: 'info',
                    message: {
                        id: 'integration_altinn_message_valid_id',
                        defaultMessage: 'Sick leaves can only be created for users with a valid Norwegian identification number.'
                    }
                },
                {
                    type: 'info',
                    message: {
                        id: 'integration_altinn_message_sick_leave_follow_up_process',
                        defaultMessage: 'For now, the sick leave follow-up process, if activated, will only start for <strong>the first sick leave period</strong> coming from NAV / Altinn.\n\nIf a sick leave period is extended, the sick leave follow-up process will stop after the first sick leave registration has ended.\n\n<sickLeaveLink>Read more about the sick leave process</sickLeaveLink>'
                    }
                }
            ]
        }
    }),
    tripletex: () => ({
        createNewUserInIntegration: {
            messages: [
                {
                    type: 'warning',
                    message: {
                        id: 'integration_tripletex_configure_settings_disclaimer',
                        defaultMessage: 'When adding users without Date of birth set, they will be created in Tripletex, but be inactive. To activate them, add their birth dates in Huma and then manually resynchronize.'
                    }
                }
            ]
        },
        createNewSalariesInIntegration: {
            messages: [
                {
                    type: 'info',
                    message: {
                        id: 'integration_tripletex_disclaimer_salary_transfer_info',
                        defaultMessage: 'All newly created or updated salary data in Huma will be automatically transferred to Tripletex. If a salary’s start date does not match an existing position in Tripletex, a new position will be created.'
                    }
                },
                {
                    type: 'warning',
                    message: {
                        id: 'integration_tripletex_disclaimer_salary_transfer_direction',
                        defaultMessage: 'Salary data will only be sent from Huma to Tripletex.'
                    }
                },
                {
                    type: 'warning',
                    message: {
                        id: 'integration_tripletex_disclaimer_salary_transfer_limitation',
                        defaultMessage: 'Only salaries created or updated after the integration is activated will be transferred to Tripletex.'
                    }
                }
            ]
        }
    }),
    unimicro: () => ({
        subEntityOrganizationNumber: {
            includeWithoutValue: true,
            messages: [
                {
                    type: 'info',
                    message: {
                        id: 'integration_unimicro_message_organization_number_info',
                        defaultMessage: 'If your user in Unimicro has access to more than one company, you need to provide an organization number.\n\nIf the company you have access to has more than one subentity in Unimicro, you need to provide a subentity’s organization number.'
                    }
                }
            ]
        }
    }),
    'visma.net': () => ({
        createNewSalariesInIntegration: {
            messages: [
                {
                    type: 'info',
                    message: {
                        id: 'integration_visma.net_disclaimer_salary_transfer_info',
                        defaultMessage: 'All newly created or updated salary data in Huma will be automatically transferred to Visma.net Payroll. If a salary’s start date does not match an existing position in Visma.net Payroll, a new position will be created.'
                    }
                },
                {
                    type: 'warning',
                    message: {
                        id: 'integration_visma.net_disclaimer_salary_transfer_direction',
                        defaultMessage: 'Salary data will only be sent from Huma to Visma.net Payroll.'
                    }
                },
                {
                    type: 'warning',
                    message: {
                        id: 'integration_visma.net_disclaimer_salary_transfer_limitation',
                        defaultMessage: 'Only salaries created or updated after the integration is activated will be transferred to Visma.net Payroll.'
                    }
                }
            ]
        }
    })
}


const getGenericDisclaimers = ({ absenceConfigured }) => ({
    createNewAbsencesInIntegration: {
        excludeWhenConnected: true,
        includeWithoutValue: !absenceConfigured,
        message: {
            type: 'info',
            message: {
                id: absenceConfigured ?
                    'integration_absence_message_map_types'
                    : 'integration_absence_message_activate_module',
                defaultMessage: absenceConfigured ?
                    'Map absence types in the next step.'
                    : 'Please set up absence before it can be sent to {integration}.'
            }
        }
    }
})